import { keyBy, pickBy } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

import { MarketStatus, RunnerStatusMarketDetails } from '@swf/generated/types/api/sports';
import type {
  LegTypes,
  MarketId,
  MarketType,
  RunnerId,
  SelectionId
} from '@swf/generated/types/api/sports';
import useBetSlip from '@swf/sports/hooks/useBetSlip';
import useSelectionWatcher from '@swf/sports/hooks/useSelectionWatcher';

import type { SelectionProps } from './Selection';
import { Button } from './components/Button/Button';

interface SelectionRunner {
  selectionId: SelectionId;
  runnerStatus?: RunnerStatusMarketDetails;
}

interface SelectionMarket {
  legTypes: LegTypes;
  marketId: MarketId;
  marketStatus: MarketStatus;
  marketType: MarketType;
  runners: SelectionRunner[];
}

export interface Props extends SelectionProps {
  markets: Record<MarketId, SelectionMarket>;
  runnerIds: RunnerId[];
}

const getSelectionRunnersBySelectionIds = ({
  markets,
  runnerIds
}: Pick<Props, 'markets' | 'runnerIds'>): Record<SelectionId, SelectionRunner> =>
  keyBy(
    Object.values(markets)
      .map((market) => market.runners)
      .flat()
      .filter((runner) =>
        runnerIds.find((runnerId) => runnerId.selectionId === runner.selectionId)
      ),
    'selectionId'
  );

export const MultipleSelections: React.FC<Props> = (props) => {
  const { eventId, eventTypeId, competitionId, markets, odds, runnerIds, startTime } = props;

  const selectionRunners = getSelectionRunnersBySelectionIds({ markets, runnerIds });
  const selectionMarkets = pickBy(markets, (market) =>
    runnerIds.map(({ marketId }) => marketId).includes(market.marketId)
  );

  const { addSelectionsToBetSlip, isEverySelectionInTheBetSlip, removeSelectionsFromBetSlip } =
    useBetSlip();

  const isSelected = isEverySelectionInTheBetSlip(runnerIds);
  const [selected, setSelected] = useState(isSelected);
  useEffect(() => {
    setSelected(isSelected);
  }, [isSelected]);

  const oddsHistory = useSelectionWatcher(odds);

  const onClick = useCallback(() => {
    if (selected) {
      removeSelectionsFromBetSlip(runnerIds);
    } else {
      addSelectionsToBetSlip(
        runnerIds.map(({ selectionId, marketId }) => ({
          selectionId,
          marketId,
          eventId,
          eventTypeId,
          competitionId,
          startTime,
          odds,
          legType:
            selectionMarkets[marketId].legTypes.find((legType) => legType === 'SIMPLE_SELECTION') ??
            selectionMarkets[marketId].legTypes[0],
          runnerStatus: selectionRunners[selectionId].runnerStatus,
          marketType: selectionMarkets[marketId].marketType
        }))
      );
    }
  }, [
    addSelectionsToBetSlip,
    competitionId,
    eventId,
    eventTypeId,
    odds,
    removeSelectionsFromBetSlip,
    runnerIds,
    selected,
    startTime,
    selectionMarkets,
    selectionRunners
  ]);

  const isLocked =
    Object.values(selectionRunners).some(
      ({ runnerStatus }) => runnerStatus !== RunnerStatusMarketDetails.ACTIVE
    ) ||
    Object.values(selectionMarkets).some(({ marketStatus }) => marketStatus !== MarketStatus.OPEN);

  const isDisabled = (isLocked || !odds) && !selected;

  return (
    <Button
      isDisabled={isDisabled}
      isLocked={isLocked}
      oddsHistory={oddsHistory}
      onClick={onClick}
      selected={selected}
      shouldAnimate={!isLocked}
      isStartingPriceMarket={false}
    />
  );
};
